import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Cell, Pie, PieChart, Tooltip, TooltipProps } from 'recharts';

import { ReactComponent as PinActiveIcon } from '../../../../assets/icons/pinActiveIcon.svg';
import { ReactComponent as PinIcon } from '../../../../assets/icons/PinIcon.svg';
import { chartTimePeriod } from '../../../../config/enum';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { setNotificationMessage } from '../../../../redux/slices/NotificationManagementSlice';
import { clearActivePin, setActivePin } from '../../../../redux/slices/ReportSlice';
import CustomToggleButton from '../../../../shared-components/custom-toggle-button/CustomToggleButton';
import {
  fillMissingLast24HrsGeneric,
  fillMissingLastNDaysGeneric,
  fillMissingMonthsGeneric,
} from '../../../../shared-functions/ChartFunctions';
import report from '../../../../utils/api/reports';

interface ChartDataProperties {
  label: string;
  maintenanceCompletedCount: number;
  maintenanceInProgressCount: number;
  maintenancePostponedCount: number;
  [key: string]: string | number;
}

/**
 * MaintenanceReportChart
 */
function MaintenanceReportChart() {
  const { t } = useTranslation('report');
  const dispatch = useAppDispatch();
  const reportsData = useAppSelector((state) => state.reportData);
  const [barGraphData, setBarGraphData] = useState<ChartDataProperties[]>([]);

  /**
   * Fetches the power consumed report data for the specified time range and updates the chart data.
   * @param range - The time period for which to fetch the report ('last24hours', 'last7days', 'lastMonth', or 'last12months').
   */
  const fetchPowerConsumedReport = async (
    range: 'last24hours' | 'last7days' | 'lastMonth' | 'last12months',
  ) => {
    try {
      const response = await report.fetchReportchart({
        timePeriod: range,
        reportType: 'maintenanceScheduleStatus',
        accountCustomId: reportsData?.accountId,
      });

      if (response.status === 200) {
        let transformedData: ChartDataProperties[];

        switch (range) {
          case 'last12months':
            transformedData = fillMissingMonthsGeneric(response.data).map((item) => ({
              label: item.label,
              maintenanceCompletedCount: (item as any).maintenanceCompletedCount || 0,
              maintenanceInProgressCount: (item as any).maintenanceInProgressCount || 0,
              maintenancePostponedCount: (item as any).maintenancePostponedCount || 0,
            }));
            break;
          case 'lastMonth':
            transformedData = fillMissingLastNDaysGeneric(response.data, 30).map((item) => ({
              label: item.label,
              maintenanceCompletedCount: (item as any).maintenanceCompletedCount || 0,
              maintenanceInProgressCount: (item as any).maintenanceInProgressCount || 0,
              maintenancePostponedCount: (item as any).maintenancePostponedCount || 0,
            }));
            break;
          case 'last7days':
            transformedData = fillMissingLastNDaysGeneric(response.data, 7).map((item) => ({
              label: item.label,
              maintenanceCompletedCount: (item as any).maintenanceCompletedCount || 0,
              maintenanceInProgressCount: (item as any).maintenanceInProgressCount || 0,
              maintenancePostponedCount: (item as any).maintenancePostponedCount || 0,
            }));
            break;
          case 'last24hours':
            transformedData = fillMissingLast24HrsGeneric(response.data).map((item) => ({
              label: item.label,
              maintenanceCompletedCount: (item as any).maintenanceCompletedCount || 0,
              maintenanceInProgressCount: (item as any).maintenanceInProgressCount || 0,
              maintenancePostponedCount: (item as any).maintenancePostponedCount || 0,
            }));
            break;
          default:
            transformedData = [];
        }

        setBarGraphData(transformedData);
      }
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || 'Something went wrong!',
          },
        }),
      );
    }
  };

  /**
   * CustomTooltip displays a tooltip with maintenanceCompletedCount and maintenanceInProgressCount booking hours.
   * @param props - TooltipProps from recharts containing active state and payload.
   */
  function CustomTooltip({ active, payload }: TooltipProps<number, string>) {
    if (active && payload && payload.length > 0) {
      const data = payload[0].payload;
      return (
        <div className="connector-status-custom-tooltip">
          <p
            className="label"
            style={{ color: data?.payload?.color }}>{`${data.name} : ${data.value}`}</p>
        </div>
      );
    }
    return null;
  }

  /**
   * Handles pin click to toggle active state for a given report name.
   * @param name - The name of the report pin to toggle.
   */
  const handleClick = (name: string) => {
    if (reportsData.activePinName.includes(name)) {
      dispatch(clearActivePin(name));
    } else {
      dispatch(setActivePin(name));
    }
  };

  useEffect(() => {
    fetchPowerConsumedReport('last24hours');
  }, []);
  return (
    <div className="energy-report-chart-wrap">
      <div className="energy-report-container">
        <div className="report-header">
          <div className="report-title">
            <button
              type="button"
              aria-label="Pin energy consumption trend based on revenue tiers"
              className={`pin-button ${reportsData.activePinName.includes('maintenanceScheduleTask') ? 'active' : ''
                }`}
              onClick={() => handleClick('maintenanceScheduleTask')}>
              {reportsData.activePinName.includes('maintenanceScheduleTask') ? (
                <span className="pin-icon">
                  <PinActiveIcon />
                </span>
              ) : (
                <span className="pin-icon-wrapper">
                  <span className="pin-icon default">
                    <PinIcon />
                  </span>
                </span>
              )}
            </button>
            {t('bookingsMaintenanceCountCancelled')}
          </div>
          <CustomToggleButton
            labels={chartTimePeriod.map((duration) => t(duration.label))}
            onToggle={(index) =>
              fetchPowerConsumedReport(
                chartTimePeriod[index]?.value || 'last12months',
              )
            }
            defaultActiveIndex={0}
            type="chart"
          />
        </div>

        <div className="energy-report-container">
          <div className="piechart-container">
            <PieChart width={250} height={240} margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
              <Pie
                data={[
                  {
                    name: t('completed'),
                    value: barGraphData.reduce(
                      (sum, d) => sum + (d.maintenanceCompletedCount || 0),
                      0,
                    ),
                    color: '#2E9E3A',
                  },
                  {
                    name: t('inProgress'),
                    value: barGraphData.reduce(
                      (sum, d) => sum + (d.maintenanceInProgressCount || 0),
                      0,
                    ),
                    color: '#4ABDFB',
                  },
                  {
                    name: t('inProgress'),
                    value: barGraphData.reduce(
                      (sum, d) => sum + (d.maintenancePostponedCount || 0),
                      0,
                    ),
                    color: '#F79009',
                  },
                ]}
                cx={125}
                cy={125}
                innerRadius={60}
                outerRadius={100}
                paddingAngle={2}
                dataKey="value"
                isAnimationActive={false} // Better for touch devices
              >
                {[
                  { name: 'maintenanceCompletedCount', color: '#2E9E3A' },
                  { name: 'maintenanceInProgressCount', color: '#4ABDFB' },
                  { name: 'maintenancePostponedCount', color: '#F79009' },
                ].map((entry) => (
                  <Cell key={entry.name} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip
                content={<CustomTooltip />}
                wrapperStyle={{ zIndex: 1000 }} // Ensure tooltip appears above other elements
              />
            </PieChart>

            <div className="total-sessions-wrap">
              <p>{t('totalCount')}</p>
              <span>
                {barGraphData.reduce(
                  (sum, d) =>
                    sum + (d.maintenanceCompletedCount || 0) + (d.maintenanceInProgressCount || 0),
                  0,
                )}
              </span>
            </div>
          </div>
          <div className="PieChart-heading">
            <div className="pie-chart-label">
              {[
                {
                  name: t('completed'),
                  value: barGraphData.reduce(
                    (sum, d) => sum + (d.maintenanceCompletedCount || 0),
                    0,
                  ),
                  color: '#2E9E3A',
                },
                {
                  name: t('inProgress'),
                  value: barGraphData.reduce(
                    (sum, d) => sum + (d.maintenanceInProgressCount || 0),
                    0,
                  ),
                  color: '#4ABDFB',
                },
                {
                  name: t('postponed'),
                  value: barGraphData.reduce(
                    (sum, d) => sum + (d.maintenanceInProgressCount || 0),
                    0,
                  ),
                  color: '#F79009',
                },
              ].map((eachData) => (
                <div key={eachData.name} className="each-label">
                  <div
                    className="each-label-color"
                    style={{ '--label-color': eachData.color } as React.CSSProperties}
                  />
                  <p>{eachData.name}</p>
                  <div className="each-label-value">
                    <span>{eachData.value}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default MaintenanceReportChart;
